import useFileUpload from './useFileUpload';
import useDragAndDrop from './useDragAndDrop';
import useInvalidFeedback from './useInvalidFeedback';

interface UseUploadAreaOptions {
  accept: string;
  value?: string | null;
  hasUploadError?: boolean;
  onUpload: (file: File) => boolean | Promise<boolean>;
  onRemove?: () => void;
}

/**
 * Combined hook for UploadArea that composes file upload, drag-and-drop, and invalid feedback hooks.
 */
const useUploadArea = ({
  accept,
  value,
  hasUploadError,
  onUpload,
  onRemove,
}: UseUploadAreaOptions) => {
  const { isFileInvalid, showInvalidFeedback, clearInvalidFeedback } =
    useInvalidFeedback();

  const fileUpload = useFileUpload({
    accept,
    value,
    hasUploadError,
    onUpload,
    onRemove,
    showInvalidFeedback,
  });

  const dragAndDrop = useDragAndDrop({
    accept,
    validateAndProcessFile: fileUpload.validateAndProcessFile,
  });

  const showInvalidState =
    dragAndDrop.isDragInvalid || isFileInvalid || !!hasUploadError;

  return {
    // File upload
    inputRef: fileUpload.inputRef,
    previewUrl: fileUpload.previewUrl,
    isRemoving: fileUpload.isRemoving,
    handleChange: fileUpload.handleChange,
    handleRemove: fileUpload.handleRemove,
    openFilePicker: fileUpload.openFilePicker,
    // Drag and drop
    isDragging: dragAndDrop.isDragging,
    handleDrop: dragAndDrop.handleDrop,
    handleDragOver: dragAndDrop.handleDragOver,
    handleDragEnter: dragAndDrop.handleDragEnter,
    handleDragLeave: dragAndDrop.handleDragLeave,
    // Invalid feedback
    showInvalidState,
    clearInvalidFeedback,
  };
};

export default useUploadArea;
